/**
 * Speaking to the assistant.
 *
 * The transport is `BrowserVoiceSession`, already built for the design studio:
 * it captures the mic as PCM16, streams it to the backend over a websocket, and
 * plays the reply. It solves the two hard parts already — barge-in with echo
 * suppression (TTS is routed through a MediaStream so the browser's echo
 * canceller uses it as its reference), and a spoken "stop" intercepted at the
 * transport before the model sees it, because otherwise the model just decides
 * to call a tool again and never stops.
 *
 * This hook is the WeaveIn half: mint a session the safe way, hold one session
 * at a time, and surface state the panel can render.
 *
 * WHAT IT DOES NOT DO
 *
 * No fallback to the browser's own SpeechRecognition. The design studio falls
 * back to it when Deepgram is unconfigured — fine for marketing copy, wrong
 * here. That API ships audio to the browser vendor's servers, which is a
 * different data-handling story than the one this product makes about member
 * information. When voice is unavailable the answer is no microphone, not a
 * quieter one.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BrowserVoiceSession,
  type VoiceApproval,
  type VoiceState,
} from '@/src/platform/modules/web-presence/lib/browserVoice';
import { copilotVoiceSocketUrl, createCopilotVoiceSession } from '@/services/copilotStream';

export interface VoiceCaption {
  role: 'user' | 'agent';
  text: string;
}

export interface CopilotVoiceController {
  /** Gated actions waiting on a human, in the order they were proposed. */
  approvals: VoiceApproval[];
  /** Confirm one. Sends its id; the server holds what it showed. */
  confirm: (id: string) => void;
  /** Take a card away without running it. */
  dismiss: (id: string) => void;
  /** How each one turned out, once it ran. */
  results: Record<string, { ok: boolean; undo?: string; error?: string }>;
  /** null when idle. */
  state: VoiceState | null;
  /** True while a session is being minted, connected, or is running. */
  active: boolean;
  /** Set when the last attempt failed, for the panel to show. Cleared on retry. */
  error: string | null;
  start: () => void;
  stop: () => void;
}

export function useCopilotVoice(
  onCaption: (caption: VoiceCaption) => void,
  language?: string,
): CopilotVoiceController {
  const [state, setState] = useState<VoiceState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approvals, setApprovals] = useState<VoiceApproval[]>([]);
  const [results, setResults] = useState<
    Record<string, { ok: boolean; undo?: string; error?: string }>
  >({});
  const sessionRef = useRef<BrowserVoiceSession | null>(null);
  // Read inside the async start without making it a dependency, so a re-render
  // mid-connection cannot leave the callback pointing at a stale setter.
  const captionRef = useRef(onCaption);
  captionRef.current = onCaption;

  const stop = useCallback(() => {
    sessionRef.current?.stop();
    sessionRef.current = null;
    setState(null);
    // Cards belong to the session that proposed them. Leaving them on screen
    // after the socket closes offers a Confirm that can only fail.
    setApprovals([]);
  }, []);

  const confirm = useCallback((id: string) => {
    sessionRef.current?.confirm(id);
    // Removed on send, not on result: the card has been acted on, and a second
    // press is a second write. The outcome arrives as a spoken line and in
    // `results`.
    setApprovals((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const dismiss = useCallback((id: string) => {
    setApprovals((prev) => prev.filter((a) => a.id !== id));
  }, []);

  // A live microphone must not outlive the panel. Without this, closing the
  // Copilot leaves the socket open and the mic recording — the user has no
  // control left that would stop it.
  useEffect(() => () => sessionRef.current?.stop(), []);

  const start = useCallback(async () => {
    if (sessionRef.current) {
      stop();
      return;
    }
    setError(null);
    // Set before the await: minting takes a round trip, and a button that does
    // nothing for a second reads as broken.
    setState('connecting');

    const minted = await createCopilotVoiceSession(language);

    // `streaming: false` means speech is not configured on the server. Say so
    // rather than silently doing nothing — and rather than falling back to a
    // transport with a different data-handling story.
    if (!minted?.session_id || !minted.streaming) {
      setState(null);
      setError('Voice is not available right now.');
      return;
    }

    // The socket has no Authorization header — the browser WebSocket API cannot
    // set one — so the token goes in the first frame instead. The session id
    // alone is not enough: it travels in the URL, and these turns run with a
    // real healthcare principal behind them.
    let authToken: string | undefined;
    try {
      authToken =
        localStorage.getItem('auth_token') || localStorage.getItem('member_token') || undefined;
    } catch {
      /* private window — the server will refuse, and the panel will say so */
    }

    const session = new BrowserVoiceSession(
      copilotVoiceSocketUrl(minted.session_id),
      {
      onCaption: (role, text) => captionRef.current({ role, text }),
      onApproval: (approval) => setApprovals((prev) => [...prev, approval]),
      onApprovalResult: (id, ok, result) =>
        setResults((prev) => ({
          ...prev,
          [id]: {
            ok,
            undo: typeof result?.undo === 'string' ? result.undo : undefined,
            error: typeof result?.error === 'string' ? result.error : undefined,
          },
        })),
      onState: (s) => {
        setState(s);
        if (s === 'ended') {
          sessionRef.current = null;
          setState(null);
        }
        if (s === 'error') {
          sessionRef.current = null;
          setState(null);
          // The overwhelmingly common cause is a declined microphone prompt,
          // and "check your microphone" is actionable where "voice failed" is
          // not.
          setError('Could not start voice. Check that this site may use your microphone.');
        }
      },
      },
      { authToken },
    );
    sessionRef.current = session;
    void session.start();
  }, [language, stop]);

  return {
    state,
    active: state !== null,
    error,
    approvals,
    results,
    confirm,
    dismiss,
    start: () => void start(),
    stop,
  };
}

export default useCopilotVoice;
